/**
 * Mensagens prontas de WhatsApp (plano, seção 11.3). Modelos editáveis.
 *
 * O app escreve a mensagem e a profissional toca em enviar: nada de API paga na versão 1.
 */
import type { MessageKind, MessageTemplate } from '../schemas/entities';

export const DEFAULT_TEMPLATES: Record<MessageKind, string> = {
  confirmation: 'Oi, {nome}! Seu horário está marcado: {serviço} {dia}, às {hora}. Endereço: {endereço}. Até lá!',
  reminder: 'Oi, {nome}! Passando para lembrar do seu horário {dia} às {hora}. Se precisar remarcar, é por aqui: {link}',
  delay: 'Oi, {nome}! Estou com uns {minutos} minutos de atraso. Desculpa!',
  reschedule: 'Oi, {nome}! Precisei mudar seu horário para {dia} às {hora}. Se não der, escolha outro aqui: {link}',
  missing: 'Oi, {nome}! Faz {semanas} semanas da sua última {serviço}. Tenho horários livres esta semana: {link}',
  open_agenda: 'Minha agenda da semana está aberta! Escolha seu horário aqui: {link}',
  post_service: 'Obrigada, {nome}! Sua próxima manutenção fica boa por volta de {data_sugerida}. Quer já garantir? {link}',
};

export const TEMPLATE_LABELS: Record<MessageKind, string> = {
  confirmation: 'Confirmação',
  reminder: 'Lembrete',
  delay: 'Atraso',
  reschedule: 'Remarcação',
  missing: 'Quem sumiu',
  open_agenda: 'Agenda aberta',
  post_service: 'Pós-atendimento',
};

/** Variáveis aceitas em cada modelo (para a tela de edição). */
export const TEMPLATE_VARIABLES: Record<MessageKind, string[]> = {
  confirmation: ['nome', 'serviço', 'dia', 'hora', 'endereço'],
  reminder: ['nome', 'serviço', 'dia', 'hora', 'link'],
  delay: ['nome', 'minutos'],
  reschedule: ['nome', 'dia', 'hora', 'link'],
  missing: ['nome', 'semanas', 'serviço', 'link'],
  open_agenda: ['link'],
  post_service: ['nome', 'data_sugerida', 'link'],
};

export type TemplateVars = Partial<
  Record<'nome' | 'serviço' | 'dia' | 'hora' | 'endereço' | 'link' | 'minutos' | 'semanas' | 'data_sugerida', string>
>;

/** Aceita {serviço} ou {servico}, {endereço} ou {endereco}. */
const ALIASES: Record<string, keyof TemplateVars> = {
  servico: 'serviço',
  endereco: 'endereço',
};

/** Modelo da profissional (o mais recente) ou o padrão. */
export function templateText(kind: MessageKind, custom: MessageTemplate[] = []): string {
  const mine = custom
    .filter((t) => t.kind === kind && !t.deleted_at)
    .sort((a, b) => (b.client_updated_at ?? '').localeCompare(a.client_updated_at ?? ''))[0];
  return mine?.text ?? DEFAULT_TEMPLATES[kind];
}

/**
 * Preenche as variáveis. Variável sem valor some junto com a frase curta em volta
 * quando dá (ex.: "Endereço: {endereço}." sem endereço vira "").
 */
export function renderTemplate(text: string, vars: TemplateVars): string {
  let out = text;
  // Remove "Endereço: {endereço}." quando não há endereço (ex.: atendimento em domicílio sem endereço)
  out = out.replace(/\s*Endereço:\s*\{(endere[cç]o)\}\.?/gi, (match, rawName: string) =>
    valueOf(vars, rawName) ? match : '',
  );
  out = out.replace(/\{([^}]+)\}/g, (_m, rawName: string) => valueOf(vars, rawName) ?? '');
  return out.replace(/[ \t]{2,}/g, ' ').replace(/\s+([.,!?])/g, '$1').trim();
}

function valueOf(vars: TemplateVars, rawName: string): string | undefined {
  const name = rawName.trim().toLowerCase();
  const key = (ALIASES[name] ?? name) as keyof TemplateVars;
  const v = vars[key];
  return v === undefined || v === '' ? undefined : v;
}

export function buildMessage(kind: MessageKind, vars: TemplateVars, custom: MessageTemplate[] = []): string {
  return renderTemplate(templateText(kind, custom), vars);
}
